/**
 * SeatManager3D.js - High-Fidelity 3D Seating & Player Avatars for Crazy Cribbage
 * Manages radial player seating, stylized 3D character avatar figures, breathing idle animations,
 * diegetic floating nameplates/score HUD plaques, active turn halo illumination, and emoji reactions.
 */

import { AvatarManager, PRESET_AVATARS } from '../ui/AvatarManager.js';

export class PlayerAvatar3D {
  /**
   * @param {BABYLON.Scene} scene
   * @param {Object} BABYLON
   * @param {string} seat - 'p1', 'p2', etc.
   * @param {Object} playerData - { name, avatarId, color, score, isTurn }
   * @param {Object} options - { chairRoot, seatIndex, dims }
   */
  constructor(scene, BABYLON, seat, playerData = {}, options = {}) {
    this.scene = scene;
    this.BABYLON = BABYLON;
    this.seat = seat || 'p1';
    this.seatIndex = options.seatIndex ?? 0;
    this.chairRoot = options.chairRoot || null;
    this.dims = options.dims || {};

    this.playerData = {
      id: seat,
      name: playerData.name || (seat === 'p1' ? 'You' : `Player ${this.seatIndex + 1}`),
      avatarId: playerData.avatarId || playerData.avatar || (seat === 'p1' ? 'joker' : 'king'),
      color: playerData.color || (seat === 'p1' ? '#3b82f6' : '#ef4444'),
      score: Number(playerData.score || 0),
      isTurn: Boolean(playerData.isTurn),
      isDealer: Boolean(playerData.isDealer)
    };

    const isComputer = Boolean(
      options.isComputer !== undefined
        ? options.isComputer
        : (playerData.isComputer || playerData.isCpu || playerData.type === 'computer' || playerData.type === 'cpu' || (this.seat !== 'p1'))
    );
    this.isComputer = isComputer;
    this.isTransparent = Boolean(options.isTransparent !== undefined ? options.isTransparent : this.isComputer);
    this.transparencyAlpha = options.transparencyAlpha ?? (this.isTransparent ? 0.6 : 1.0);
    this.wrapAvatarOnHead = Boolean(options.wrapAvatarOnHead !== undefined ? options.wrapAvatarOnHead : this.isComputer);

    this.rootNode = null;
    this.torsoMesh = null;
    this.headMesh = null;
    this.collarMesh = null;
    this.armMeshes = [];
    this.faceCameoMesh = null;
    this.turnHaloMesh = null;
    this.nameplateMesh = null;
    this.nameplateTexture = null;
    this.reactionBubbleMesh = null;
    this.reactionTexture = null;
    this.reactionMaterial = null;
    this.faceCameoTexture = null;
    this.headWrapTexture = null;

    this.visible = options.visible !== false;
    this.materials = [];
    this.textures = [];
    this.idleObserver = null;
    this._reactionTimeout = null;

    this.buildAvatar();
    if (!this.visible) {
      this.setVisible(false);
    }
  }

  /**
   * Builds the complete stylized 3D seated figure, face cameo or head wrap, turn halo, and floating HUD plaque.
   */
  buildAvatar() {
    const { scene, BABYLON } = this;
    const seat = this.seat;
    const avatarInfo = AvatarManager?.getAvatarById?.(this.playerData.avatarId) || PRESET_AVATARS[0] || {};
    const badgeColorHex = avatarInfo.badgeColor || this.playerData.color || '#3b82f6';
    const badgeColor = this.hexToColor3(badgeColorHex);

    // Root Transform Node attached to chairRoot (or origin if chairRoot not provided)
    this.rootNode = new BABYLON.TransformNode(`avatarRoot_${seat}`, scene);
    if (this.chairRoot) {
      this.rootNode.parent = this.chairRoot;
    }

    const floorY = this.dims.floorY ?? -110.0;
    const seatElev = this.dims.seatElev ?? 72.0;
    const seatH = this.dims.seatH ?? 12.0;
    const seatSurfaceY = floorY + seatElev + seatH / 2;

    // Local seated offset directly on chair cushion, resting back against the chair backrest
    this.rootNode.position.set(0, seatSurfaceY, -14.0);

    const doubleSide = (BABYLON.Mesh && BABYLON.Mesh.DOUBLESIDE != null) ? BABYLON.Mesh.DOUBLESIDE : 2;

    // 1. Stylized Torso / Jacket Mesh - fits snugly between chair armrests
    this.torsoMesh = BABYLON.MeshBuilder.CreateBox(`torso_${seat}`, {
      width: 64.0,
      height: 56.0,
      depth: 32.0
    }, scene);
    this.torsoMesh.position.set(0, 28.0, 0);
    this.torsoMesh.parent = this.rootNode;
    this.torsoMesh.receiveShadows = true;

    const torsoMat = new BABYLON.PBRMaterial(`matTorso_${seat}`, scene);
    torsoMat.albedoColor = badgeColor;
    torsoMat.roughness = 0.65;
    torsoMat.metallic = 0.05;
    if (this.isTransparent) {
      torsoMat.alpha = Math.min(this.transparencyAlpha, 0.55);
      if (BABYLON.PBRMaterial?.PBRMATERIAL_ALPHABLEND != null) {
        torsoMat.transparencyMode = BABYLON.PBRMaterial.PBRMATERIAL_ALPHABLEND;
      }
      torsoMat.backFaceCulling = true;
      this.torsoMesh.visibility = Math.min(this.transparencyAlpha, 0.6);
    }
    this.torsoMesh.material = torsoMat;
    this.materials.push(torsoMat);

    // 2. Collar & Neck Band
    const collarMesh = BABYLON.MeshBuilder.CreateCylinder(`collar_${seat}`, {
      diameter: 24.0,
      height: 6.0,
      tessellation: 18
    }, scene);
    collarMesh.position.set(0, 58.0, 0);
    collarMesh.parent = this.rootNode;
    this.collarMesh = collarMesh;

    const collarMat = new BABYLON.PBRMaterial(`matCollar_${seat}`, scene);
    collarMat.albedoColor = new BABYLON.Color3(0.92, 0.78, 0.32); // Gold trim
    collarMat.metallic = 0.88;
    collarMat.roughness = 0.25;
    if (this.isTransparent) {
      collarMat.alpha = Math.min(this.transparencyAlpha + 0.1, 0.7);
      if (BABYLON.PBRMaterial?.PBRMATERIAL_ALPHABLEND != null) {
        collarMat.transparencyMode = BABYLON.PBRMaterial.PBRMATERIAL_ALPHABLEND;
      }
      collarMesh.visibility = Math.min(this.transparencyAlpha + 0.1, 0.7);
    }
    collarMesh.material = collarMat;
    this.materials.push(collarMat);

    // 3. Stylized Head & Hair Crown
    this.headMesh = BABYLON.MeshBuilder.CreateSphere(`head_${seat}`, {
      diameter: 36.0,
      segments: 20
    }, scene);
    this.headMesh.position.set(0, 78.0, 0);
    this.headMesh.parent = this.rootNode;
    // Align equator center (U=0.5) to face +Z directly forward towards the table
    if (this.headMesh.rotation) {
      this.headMesh.rotation.y = (Math.PI / 2);
    }

    const headMat = new BABYLON.PBRMaterial(`matHead_${seat}`, scene);
    headMat.albedoColor = new BABYLON.Color3(0.93, 0.85, 0.78); // Warm porcelain skin
    headMat.roughness = 0.38;
    headMat.metallic = 0.05;
    if (this.isTransparent) {
      headMat.alpha = this.transparencyAlpha;
      if (BABYLON.PBRMaterial?.PBRMATERIAL_ALPHABLEND != null) {
        headMat.transparencyMode = BABYLON.PBRMaterial.PBRMATERIAL_ALPHABLEND;
      }
      headMat.backFaceCulling = false;
      this.headMesh.visibility = this.transparencyAlpha;
    }
    this.headMesh.material = headMat;
    this.materials.push(headMat);

    // 4. Portrait Cameo Medallion (Front face of head, facing +Z towards table center)
    this.faceCameoMesh = BABYLON.MeshBuilder.CreatePlane(`faceCameo_${seat}`, {
      width: 28.0,
      height: 28.0,
      sideOrientation: doubleSide
    }, scene);
    this.faceCameoMesh.position.set(0, 78.0, 18.2);
    this.faceCameoMesh.parent = this.rootNode;
    if (this.faceCameoMesh.rotation) {
      this.faceCameoMesh.rotation.y = Math.PI;
    }

    const faceMat = new BABYLON.PBRMaterial(`matFaceCameo_${seat}`, scene);
    faceMat.roughness = 0.3;
    faceMat.metallic = 0.05;
    faceMat.backFaceCulling = false;
    this.faceCameoMesh.material = faceMat;
    this.materials.push(faceMat);

    if (this.wrapAvatarOnHead) {
      // When wrapAvatarOnHead is active, avatar artwork is wrapped directly onto headMesh sphere
      this.faceCameoMesh.isVisible = false;
      this.applyHeadWrapTexture(headMat, avatarInfo);
    } else {
      this.applyAvatarFaceTexture(faceMat, avatarInfo);
    }

    // 5. Stylized Seated Arms resting forward directly alongside chair armrests
    this.armMeshes = [];
    [-36.0, 36.0].forEach((ax, aIdx) => {
      const arm = BABYLON.MeshBuilder.CreateBox(`arm_${seat}_${aIdx}`, {
        width: 10.0,
        height: 32.0,
        depth: 30.0
      }, scene);
      arm.position.set(ax, 24.0, 6.0);
      arm.parent = this.rootNode;
      arm.material = torsoMat;
      arm.receiveShadows = true;
      if (this.isTransparent) {
        arm.visibility = Math.min(this.transparencyAlpha, 0.6);
      }
      this.armMeshes.push(arm);

      // Forearm / Hand resting forward near armrest & table
      const hand = BABYLON.MeshBuilder.CreateBox(`hand_${seat}_${aIdx}`, {
        width: 9.0,
        height: 9.0,
        depth: 16.0
      }, scene);
      hand.position.set(ax, 9.0, 24.0);
      hand.parent = this.rootNode;
      hand.material = headMat;
      if (this.isTransparent) {
        hand.visibility = Math.min(this.transparencyAlpha, 0.6);
      }
      this.armMeshes.push(hand);
    });

    // 6. Active Turn Halo Ring (Floating above head, pulses golden when isTurn is true)
    const haloMesh = (BABYLON.MeshBuilder.CreateTorus)
      ? BABYLON.MeshBuilder.CreateTorus(`turnHalo_${seat}`, { diameter: 40.0, thickness: 3.4, tessellation: 24 }, scene)
      : BABYLON.MeshBuilder.CreateCylinder(`turnHalo_${seat}`, { diameter: 40.0, height: 3.4, tessellation: 24 }, scene);

    haloMesh.position.set(0, 101.0, 0);
    haloMesh.parent = this.rootNode;
    haloMesh.isVisible = this.playerData.isTurn;

    const haloMat = new BABYLON.PBRMaterial(`matHalo_${seat}`, scene);
    haloMat.albedoColor = new BABYLON.Color3(1.0, 0.85, 0.2);
    haloMat.emissiveColor = new BABYLON.Color3(1.0, 0.78, 0.15);
    haloMat.metallic = 0.9;
    haloMat.roughness = 0.2;
    haloMesh.material = haloMat;
    this.turnHaloMesh = haloMesh;
    this.materials.push(haloMat);

    // 7. Floating Nameplate & Live Score HUD Plaque
    this.nameplateMesh = BABYLON.MeshBuilder.CreatePlane(`nameplate_${seat}`, {
      width: 68.0,
      height: 24.0,
      sideOrientation: doubleSide
    }, scene);
    this.nameplateMesh.position.set(0, 120.0, 0);
    this.nameplateMesh.parent = this.rootNode;
    this.nameplateMesh.billboardMode = (BABYLON.Mesh && BABYLON.Mesh.BILLBOARDMODE_ALL != null)
      ? BABYLON.Mesh.BILLBOARDMODE_ALL
      : 7;

    const nameplateMat = new BABYLON.PBRMaterial(`matNameplate_${seat}`, scene);
    nameplateMat.roughness = 0.35;
    nameplateMat.metallic = 0.05;
    nameplateMat.backFaceCulling = false;
    this.nameplateMesh.material = nameplateMat;
    this.materials.push(nameplateMat);

    this.updateNameplateTexture(nameplateMat);

    // 8. Reaction Speech Bubble (Hidden until reaction triggered)
    this.reactionBubbleMesh = BABYLON.MeshBuilder.CreatePlane(`reactionBubble_${seat}`, {
      width: 36.0,
      height: 36.0,
      sideOrientation: doubleSide
    }, scene);
    this.reactionBubbleMesh.position.set(0, 142.0, 0);
    this.reactionBubbleMesh.parent = this.rootNode;
    this.reactionBubbleMesh.isVisible = false;
    this.reactionBubbleMesh.billboardMode = (BABYLON.Mesh && BABYLON.Mesh.BILLBOARDMODE_ALL != null)
      ? BABYLON.Mesh.BILLBOARDMODE_ALL
      : 7;

    // 9. Idle Breathing Cycle Animation
    this.startIdleAnimation();
  }

  /**
   * Applies the avatar face texture or draws a stylized circular cameo.
   * @param {BABYLON.PBRMaterial} faceMat
   * @param {Object} avatarInfo
   */
  async applyAvatarFaceTexture(faceMat, avatarInfo) {
    const { scene, BABYLON } = this;
    if (!BABYLON || !BABYLON.DynamicTexture) return;

    try {
      let tex = this.faceCameoTexture;
      if (!tex || (typeof tex.isDisposed === 'function' && tex.isDisposed())) {
        tex = new BABYLON.DynamicTexture(`texFaceCameo_${this.seat}`, { width: 256, height: 256 }, scene, false);
        this.faceCameoTexture = tex;
        this.textures.push(tex);
      }
      const ctx = tex.getContext();
      if (!ctx) return;

      const w = 256;
      const h = 256;
      const r = 120;

      // Circular clipping mask
      ctx.clearRect(0, 0, w, h);
      ctx.save();
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, r, 0, Math.PI * 2);
      ctx.clip();

      let drawn = false;
      if (typeof AvatarManager?.createAvatarCanvas === 'function') {
        const c = await AvatarManager.createAvatarCanvas(this.playerData.avatarId, 256, 256);
        if (c && ctx.drawImage) {
          ctx.drawImage(c, 0, 0, w, h);
          drawn = true;
        }
      }

      if (!drawn) {
        // Fallback procedural avatar medallion
        ctx.fillStyle = avatarInfo.badgeColor || this.playerData.color || '#3b82f6';
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 110px "Segoe UI", Roboto, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const initial = (this.playerData.name || this.seat).charAt(0).toUpperCase();
        ctx.fillText(initial, w / 2, h / 2 + 8);
      }

      ctx.restore();

      // Gold cameo outer rim
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 10;
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, r - 5, 0, Math.PI * 2);
      ctx.stroke();

      tex.update();
      if (faceMat.albedoTexture && faceMat.albedoTexture !== tex) {
        try { faceMat.albedoTexture.dispose(); } catch (_) {}
      }
      faceMat.albedoTexture = tex;
    } catch (_) {}
  }

  /**
   * Generates a 360-degree panoramic spherical head texture wrapping the avatar portrait
   * across the front of the head with stylized hair/crown and side/back trims.
   * @param {BABYLON.PBRMaterial} headMat
   * @param {Object} avatarInfo
   */
  async applyHeadWrapTexture(headMat, avatarInfo = {}) {
    const { scene, BABYLON } = this;
    if (!BABYLON || !BABYLON.DynamicTexture) return;

    try {
      const w = 512;
      const h = 256;
      let tex = this.headWrapTexture;
      if (!tex || (typeof tex.isDisposed === 'function' && tex.isDisposed())) {
        tex = new BABYLON.DynamicTexture(`texHeadWrap_${this.seat}`, { width: w, height: h }, scene, false);
        // Invert vertical UV sampling because Babylon CreateSphere maps the top pole (+Y) to v=0,
        // whereas standard texture coordinate v=1 corresponds to the top of the canvas image.
        tex.vScale = -1;
        tex.vOffset = 1;
        this.headWrapTexture = tex;
        this.textures.push(tex);
      }
      const ctx = tex.getContext();
      if (!ctx) return;

      const badgeColorHex = avatarInfo.badgeColor || this.playerData.color || '#3b82f6';

      // 1. Base Spherical Head Surface Gradient
      // Equator is h/2 (eye level). Top is h=0 (crown), bottom is h (neck).
      ctx.clearRect(0, 0, w, h);

      const bgGrad = ctx.createLinearGradient(0, 0, 0, h);
      if (this.isTransparent) {
        bgGrad.addColorStop(0, 'rgba(15, 23, 42, 0.7)');
        bgGrad.addColorStop(0.3, this.hexToRgba(badgeColorHex, 0.65));
        bgGrad.addColorStop(0.55, 'rgba(248, 250, 252, 0.75)');
        bgGrad.addColorStop(0.85, this.hexToRgba(badgeColorHex, 0.65));
        bgGrad.addColorStop(1, 'rgba(15, 23, 42, 0.7)');
      } else {
        bgGrad.addColorStop(0, '#0f172a');
        bgGrad.addColorStop(0.3, badgeColorHex);
        bgGrad.addColorStop(0.55, '#f8fafc');
        bgGrad.addColorStop(0.85, badgeColorHex);
        bgGrad.addColorStop(1, '#0f172a');
      }
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, w, h);

      // 2. Decorative side and back card suit accents
      ctx.save();
      ctx.fillStyle = this.isTransparent ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.4)';
      ctx.font = 'bold 24px serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      // Left side of head (near U=0.2)
      ctx.fillText('♠', w * 0.15, h * 0.5);
      ctx.fillText('♦', w * 0.25, h * 0.5);
      // Right side of head (near U=0.8)
      ctx.fillText('♣', w * 0.75, h * 0.5);
      ctx.fillText('♥', w * 0.85, h * 0.5);
      // Back of head (near U=0 and U=1.0)
      ctx.fillText('👑', w * 0.05, h * 0.32);
      ctx.fillText('👑', w * 0.95, h * 0.32);
      ctx.restore();

      // Top hair / crown filigree band
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(0, h * 0.22);
      ctx.lineTo(w, h * 0.22);
      ctx.stroke();

      // 3. Render Avatar Face Portrait onto Front Hemisphere (centered at U=0.5 -> X=w*0.5, Y=h*0.5)
      const faceSize = Math.round(h * 0.72);
      const fx = Math.round((w - faceSize) / 2);
      const fy = Math.round((h - faceSize) / 2);

      let drawn = false;
      if (typeof AvatarManager?.createAvatarCanvas === 'function') {
        const c = await AvatarManager.createAvatarCanvas(this.playerData.avatarId, faceSize, faceSize);
        if (c && ctx.drawImage) {
          ctx.save();
          // Subtle luminous face halo behind portrait
          const glowGrad = ctx.createRadialGradient(w / 2, h / 2, faceSize * 0.2, w / 2, h / 2, faceSize * 0.55);
          glowGrad.addColorStop(0, this.hexToRgba(badgeColorHex, 0.5));
          glowGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
          ctx.fillStyle = glowGrad;
          ctx.fillRect(fx - 10, fy - 10, faceSize + 20, faceSize + 20);

          ctx.drawImage(c, fx, fy, faceSize, faceSize);
          ctx.restore();
          drawn = true;
        }
      }

      if (!drawn) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(w / 2, h / 2, faceSize / 2, 0, Math.PI * 2);
        ctx.fillStyle = badgeColorHex;
        ctx.fill();
        ctx.strokeStyle = '#f59e0b';
        ctx.lineWidth = 4;
        ctx.stroke();
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 72px "Segoe UI", Roboto, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const initial = (this.playerData.name || this.seat).charAt(0).toUpperCase();
        ctx.fillText(initial, w / 2, h / 2 + 4);
        ctx.restore();
      }

      tex.update();
      if (headMat.albedoTexture && headMat.albedoTexture !== tex) {
        try { headMat.albedoTexture.dispose(); } catch (_) {}
      }
      headMat.albedoTexture = tex;
      if (headMat.useAlphaFromAlbedoTexture !== undefined) {
        headMat.useAlphaFromAlbedoTexture = true;
      }
    } catch (_) {}
  }

  /**
   * Helper: converts hex color string and alpha to CSS rgba string.
   * @param {string} hex
   * @param {number} alpha
   * @returns {string}
   */
  hexToRgba(hex, alpha = 1.0) {
    if (!hex || typeof hex !== 'string') return `rgba(59, 130, 246, ${alpha})`;
    const clean = hex.replace('#', '');
    const num = parseInt(clean, 16);
    if (isNaN(num)) return `rgba(59, 130, 246, ${alpha})`;
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  /**
   * Updates transparency for computer player avatar figure.
   * @param {boolean} isTransparent
   * @param {number} [alpha=0.6]
   */
  setTransparent(isTransparent, alpha = 0.6) {
    this.isTransparent = Boolean(isTransparent);
    this.transparencyAlpha = this.isTransparent ? alpha : 1.0;

    const blendMode = (this.BABYLON?.PBRMaterial?.PBRMATERIAL_ALPHABLEND != null)
      ? this.BABYLON.PBRMaterial.PBRMATERIAL_ALPHABLEND
      : 2;

    const applyTrans = (mesh, mat, a) => {
      if (mesh) mesh.visibility = this.isTransparent ? a : 1.0;
      if (mat) {
        mat.alpha = this.isTransparent ? a : 1.0;
        if (mat.transparencyMode !== undefined) {
          mat.transparencyMode = this.isTransparent ? blendMode : 0;
        }
      }
    };

    applyTrans(this.torsoMesh, this.torsoMesh?.material, Math.min(this.transparencyAlpha, 0.55));
    applyTrans(this.collarMesh, this.collarMesh?.material, Math.min(this.transparencyAlpha + 0.1, 0.7));
    applyTrans(this.headMesh, this.headMesh?.material, this.transparencyAlpha);
    if (this.armMeshes) {
      this.armMeshes.forEach(m => {
        if (m) m.visibility = this.isTransparent ? Math.min(this.transparencyAlpha, 0.6) : 1.0;
      });
    }
  }

  /**
   * Toggles avatar wrapping on head sphere vs flat floating cameo.
   * @param {boolean} wrap
   */
  setWrapAvatarOnHead(wrap) {
    this.wrapAvatarOnHead = Boolean(wrap);
    if (this.faceCameoMesh) {
      this.faceCameoMesh.isVisible = !this.wrapAvatarOnHead && this.visible;
    }
    const avatarInfo = AvatarManager?.getAvatarById?.(this.playerData.avatarId) || {};
    if (this.wrapAvatarOnHead) {
      if (this.headMesh?.material) {
        this.applyHeadWrapTexture(this.headMesh.material, avatarInfo);
      }
    } else {
      if (this.faceCameoMesh?.material) {
        this.applyAvatarFaceTexture(this.faceCameoMesh.material, avatarInfo);
      }
    }
  }

  /**
   * Generates or refreshes the floating HUD nameplate texture with name, score, and turn glow.
   * @param {BABYLON.PBRMaterial} mat
   */
  updateNameplateTexture(mat) {
    const { scene, BABYLON } = this;
    if (!BABYLON || !BABYLON.DynamicTexture) return;

    try {
      let tex = this.nameplateTexture;
      if (!tex || (typeof tex.isDisposed === 'function' && tex.isDisposed())) {
        tex = new BABYLON.DynamicTexture(`texNameplate_${this.seat}`, { width: 512, height: 180 }, scene, false);
        this.nameplateTexture = tex;
        this.textures.push(tex);
      }
      const ctx = tex.getContext();
      if (!ctx) return;

      const w = 512;
      const h = 180;
      const isTurn = Boolean(this.playerData.isTurn);
      const isDealer = Boolean(this.playerData.isDealer);

      ctx.clearRect(0, 0, w, h);

      // Card Background
      ctx.fillStyle = isTurn ? 'rgba(15, 23, 42, 0.94)' : 'rgba(15, 23, 42, 0.82)';
      ctx.beginPath();
      if (ctx.roundRect) {
        ctx.roundRect(10, 10, w - 20, h - 20, 24);
      } else {
        ctx.rect(10, 10, w - 20, h - 20);
      }
      ctx.fill();

      // Border (glowing gold on turn or dealer, slate otherwise)
      ctx.strokeStyle = isTurn ? '#f59e0b' : (isDealer ? 'rgba(245, 158, 11, 0.75)' : '#334155');
      ctx.lineWidth = isTurn ? 8 : (isDealer ? 5 : 4);
      ctx.stroke();

      // Player Name
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 44px "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      const displayName = this.playerData.name || this.seat;
      ctx.fillText(displayName.slice(0, 13), 36, h / 2 - 20);

      // Score Pill Badge
      const scoreText = `${this.playerData.score || 0} pts`;
      ctx.fillStyle = isTurn ? '#f59e0b' : '#3b82f6';
      ctx.beginPath();
      if (ctx.roundRect) {
        ctx.roundRect(36, h / 2 + 18, 136, 42, 14);
      } else {
        ctx.rect(36, h / 2 + 18, 136, 42);
      }
      ctx.fill();

      ctx.fillStyle = isTurn ? '#000000' : '#ffffff';
      ctx.font = 'bold 28px "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(scoreText, 104, h / 2 + 40);

      // Dealer Badge
      if (isDealer) {
        const dealerX = isTurn ? (w - 184) : (w - 170);
        const dealerY = isTurn ? (h / 2 - 46) : (h / 2 - 22);
        const dealerW = isTurn ? 148 : 144;
        const dealerH = isTurn ? 40 : 44;

        const grad = ctx.createLinearGradient(dealerX, dealerY, dealerX + dealerW, dealerY + dealerH);
        grad.addColorStop(0, '#f59e0b');
        grad.addColorStop(1, '#b45309');
        ctx.fillStyle = grad;

        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(dealerX, dealerY, dealerW, dealerH, 14);
        } else {
          ctx.rect(dealerX, dealerY, dealerW, dealerH);
        }
        ctx.fill();

        ctx.strokeStyle = '#fef08a';
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.fillStyle = '#0f172a';
        ctx.font = 'bold 24px "Segoe UI", Roboto, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('★ DEALER', dealerX + dealerW / 2, dealerY + dealerH / 2 + 2);
      }

      // Turn Badge (Right side indicator)
      if (isTurn) {
        const turnX = isDealer ? (w - 184) : (w - 170);
        const turnY = isDealer ? (h / 2 + 10) : (h / 2 - 20);
        const turnW = isDealer ? 148 : 130;
        const turnH = isDealer ? 40 : 44;

        ctx.fillStyle = '#10b981';
        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(turnX, turnY, turnW, turnH, 14);
        } else {
          ctx.rect(turnX, turnY, turnW, turnH);
        }
        ctx.fill();

        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 24px "Segoe UI", Roboto, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('TURN', turnX + turnW / 2, turnY + turnH / 2 + 2);
      }

      tex.update();
      if (mat.albedoTexture !== tex) {
        mat.albedoTexture = tex;
      }
    } catch (_) {}
  }

  /**
   * Updates player data (name, avatar, score, turn, dealer) and refreshes visual indicators.
   * @param {Object} data
   */
  update(data = {}) {
    let changed = false;

    if (data.name !== undefined && data.name !== this.playerData.name) {
      this.playerData.name = data.name;
      changed = true;
    }
    if (data.score !== undefined && Number(data.score) !== this.playerData.score) {
      this.playerData.score = Number(data.score);
      changed = true;
    }
    if (data.isTurn !== undefined && Boolean(data.isTurn) !== this.playerData.isTurn) {
      this.playerData.isTurn = Boolean(data.isTurn);
      if (this.turnHaloMesh) {
        this.turnHaloMesh.isVisible = this.playerData.isTurn;
      }
      changed = true;
    }
    if (data.isDealer !== undefined && Boolean(data.isDealer) !== this.playerData.isDealer) {
      this.playerData.isDealer = Boolean(data.isDealer);
      changed = true;
    }
    if (data.avatarId !== undefined && data.avatarId !== this.playerData.avatarId) {
      this.playerData.avatarId = data.avatarId;
      const avatarInfo = AvatarManager?.getAvatarById?.(this.playerData.avatarId) || {};
      if (this.wrapAvatarOnHead) {
        const headMat = this.headMesh?.material;
        if (headMat) this.applyHeadWrapTexture(headMat, avatarInfo);
      } else {
        const faceMat = this.faceCameoMesh?.material;
        if (faceMat) this.applyAvatarFaceTexture(faceMat, avatarInfo);
      }
      changed = true;
    }

    if (changed && this.nameplateMesh?.material) {
      this.updateNameplateTexture(this.nameplateMesh.material);
    }
  }

  /**
   * Sets or clears the active turn state.
   * @param {boolean} isTurn
   */
  setTurn(isTurn) {
    const nextVal = Boolean(isTurn);
    if (this.playerData.isTurn === nextVal) return;
    this.playerData.isTurn = nextVal;
    if (this.turnHaloMesh) {
      this.turnHaloMesh.isVisible = this.playerData.isTurn;
    }
    if (this.nameplateMesh?.material) {
      this.updateNameplateTexture(this.nameplateMesh.material);
    }
  }

  /**
   * Sets or clears the dealer state on this seated avatar.
   * @param {boolean} isDealer
   */
  setDealer(isDealer) {
    const nextVal = Boolean(isDealer);
    if (this.playerData.isDealer === nextVal) return;
    this.playerData.isDealer = nextVal;
    if (this.nameplateMesh?.material) {
      this.updateNameplateTexture(this.nameplateMesh.material);
    }
  }

  /**
   * Updates the displayed score.
   * @param {number} score
   */
  setScore(score) {
    const nextVal = Number(score);
    if (this.playerData.score === nextVal) return;
    this.playerData.score = nextVal;
    if (this.nameplateMesh?.material) {
      this.updateNameplateTexture(this.nameplateMesh.material);
    }
  }

  /**
   * Shows a floating emoji reaction speech bubble over the player avatar.
   * @param {string} emoji
   * @param {number} [duration=3200]
   */
  showReaction(emoji = '😎', duration = 3200) {
    if (!this.reactionBubbleMesh) return;

    if (this._reactionTimeout) {
      clearTimeout(this._reactionTimeout);
      this._reactionTimeout = null;
    }

    const { scene, BABYLON } = this;
    if (BABYLON?.DynamicTexture) {
      let tex = this.reactionTexture;
      if (!tex || (typeof tex.isDisposed === 'function' && tex.isDisposed())) {
        tex = new BABYLON.DynamicTexture(`texReaction_${this.seat}`, { width: 160, height: 160 }, scene, false);
        this.reactionTexture = tex;
        this.textures.push(tex);
      }
      const ctx = tex.getContext();
      if (ctx) {
        ctx.clearRect(0, 0, 160, 160);

        // Bubble circle
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(80, 80, 70, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 6;
        ctx.stroke();

        // Emoji
        ctx.font = '72px "Segoe UI Emoji", "Apple Color Emoji", sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(emoji, 80, 84);
        tex.update();

        let mat = this.reactionMaterial;
        if (!mat || (typeof mat.isDisposed === 'function' && mat.isDisposed())) {
          mat = new BABYLON.PBRMaterial(`matReaction_${this.seat}`, scene);
          mat.roughness = 0.2;
          this.reactionMaterial = mat;
          this.materials.push(mat);
        }
        mat.albedoTexture = tex;
        this.reactionBubbleMesh.material = mat;
      }
    }

    this.reactionBubbleMesh.isVisible = true;

    this._reactionTimeout = setTimeout(() => {
      if (this.reactionBubbleMesh) {
        this.reactionBubbleMesh.isVisible = false;
      }
    }, duration);
  }

  /**
   * Initiates gentle breathing idle animation cycle.
   */
  startIdleAnimation() {
    if (!this.scene?.onBeforeRenderObservable || !this.torsoMesh) return;
    if (this.idleObserver) {
      this.scene.onBeforeRenderObservable.remove(this.idleObserver);
      this.idleObserver = null;
    }

    const baseTorsoY = this.torsoMesh.position.y;
    const baseHeadY = this.headMesh ? this.headMesh.position.y : 76.0;
    const phaseOffset = this.seatIndex * 1.25;

    this.idleObserver = this.scene.onBeforeRenderObservable.add(() => {
      const t = performance.now() * 0.002 + phaseOffset;
      const breathe = Math.sin(t) * 0.45;
      if (this.torsoMesh) this.torsoMesh.position.y = baseTorsoY + breathe;
      if (this.headMesh) this.headMesh.position.y = baseHeadY + breathe * 1.1;
      if (this.faceCameoMesh) this.faceCameoMesh.position.y = baseHeadY + breathe * 1.1;

      // Pulse turn halo slightly if active
      if (this.playerData.isTurn && this.turnHaloMesh) {
        const pulse = 1.0 + Math.sin(t * 3.0) * 0.08;
        this.turnHaloMesh.scaling.set(pulse, 1.0, pulse);
      }
    });
  }

  /**
   * Helper: converts hex color string to Babylon Color3.
   * @param {string} hex
   * @returns {BABYLON.Color3}
   */
  hexToColor3(hex) {
    const BABYLON = this.BABYLON;
    if (!hex || typeof hex !== 'string') return new BABYLON.Color3(0.2, 0.4, 0.8);
    const clean = hex.replace('#', '');
    const num = parseInt(clean, 16);
    if (isNaN(num)) return new BABYLON.Color3(0.2, 0.4, 0.8);
    const r = ((num >> 16) & 255) / 255;
    const g = ((num >> 8) & 255) / 255;
    const b = (num & 255) / 255;
    return new BABYLON.Color3(r, g, b);
  }

  /**
   * Sets avatar visibility (used to hide the human player so hand cards are fully visible).
   * @param {boolean} visible
   */
  setVisible(visible) {
    this.visible = Boolean(visible);
    if (this.rootNode && typeof this.rootNode.setEnabled === 'function') {
      this.rootNode.setEnabled(this.visible);
    }
    if (this.torsoMesh) this.torsoMesh.isVisible = this.visible;
    if (this.headMesh) this.headMesh.isVisible = this.visible;
    if (this.collarMesh) this.collarMesh.isVisible = this.visible;
    if (this.faceCameoMesh) this.faceCameoMesh.isVisible = this.visible && !this.wrapAvatarOnHead;
    if (this.nameplateMesh) this.nameplateMesh.isVisible = this.visible;
    if (this.turnHaloMesh) this.turnHaloMesh.isVisible = this.visible && Boolean(this.playerData.isTurn);
    if (this.armMeshes) this.armMeshes.forEach(m => { if (m) m.isVisible = this.visible; });
  }

  /**
   * Disposes all meshes, textures, materials, and observers for this player avatar.
   */
  dispose() {
    if (this._reactionTimeout) {
      clearTimeout(this._reactionTimeout);
      this._reactionTimeout = null;
    }
    if (this.idleObserver && this.scene?.onBeforeRenderObservable) {
      this.scene.onBeforeRenderObservable.remove(this.idleObserver);
      this.idleObserver = null;
    }
    this.nameplateTexture = null;
    this.faceCameoTexture = null;
    this.headWrapTexture = null;
    this.reactionTexture = null;
    this.reactionMaterial = null;
    this.textures.forEach(t => { try { t.dispose(); } catch (_) {} });
    this.textures = [];
    this.materials.forEach(m => { try { m.dispose(); } catch (_) {} });
    this.materials = [];
    if (this.collarMesh) {
      try { this.collarMesh.dispose(); } catch (_) {}
      this.collarMesh = null;
    }
    if (this.armMeshes) {
      this.armMeshes.forEach(m => { try { m.dispose(); } catch (_) {} });
      this.armMeshes = [];
    }
    if (this.rootNode) {
      try { this.rootNode.dispose(false, true); } catch (_) {}
      this.rootNode = null;
    }
  }
}

/**
 * SeatManager3D - Orchestrates player seating and avatar representations around the 3D table.
 */
export class SeatManager3D {
  /**
   * @param {BABYLON.Scene} scene
   * @param {Object} BABYLON
   * @param {Object} options - { dims, seatCount, chairs }
   */
  constructor(scene, BABYLON, options = {}) {
    this.scene = scene;
    this.BABYLON = BABYLON;
    this.options = options;
    this.dims = options.dims || {};

    this.seats = new Map(); // seat ('p1', 'p2', etc.) -> PlayerAvatar3D
    this.chairRoots = options.chairRoots || [];
  }

  /**
   * Sets up 3D seated player avatars according to active roster / seat count.
   * Computer player avatars (p2, p3, etc.) are rendered semi-transparent with wrapped head textures.
   * @param {Array<Object>} players - Array of player info { id, name, avatarId, score, isTurn }
   * @param {number} [count=2]
   * @param {Array<BABYLON.TransformNode>} [chairRoots=[]]
   * @returns {Map<string, PlayerAvatar3D>}
   */
  setupSeats(players = [], count = 2, chairRoots = []) {
    this.clearAll();

    const actualRoots = (chairRoots && chairRoots.length > 0) ? chairRoots : this.chairRoots;
    const seatCount = Math.max(2, Math.min(8, count || players.length || 2));

    for (let i = 0; i < seatCount; i++) {
      const seat = `p${i + 1}`;
      const playerData = players.find(p => p.id === seat) || players[i] || {
        id: seat,
        name: i === 0 ? 'You' : `Player ${i + 1}`,
        avatarId: i === 0 ? 'joker' : (i === 1 ? 'king' : 'shark'),
        score: 0,
        isTurn: (i === 0)
      };

      const chairRoot = actualRoots[i] || null;

      // The local human player ('p1') is seated in the foreground directly in front of the camera.
      // Hiding the human player's figure ensures an unobstructed first-person view of their cards and the table,
      // while opponent figures (p2, p3, etc.) remain fully visible across the table.
      const isLocal = (seat === 'p1');
      const showFigure = this.options.showLocalPlayer === true || !isLocal;
      const isComputer = !isLocal || Boolean(playerData.isComputer || playerData.isCpu || playerData.type === 'computer' || playerData.type === 'cpu');

      const avatar = new PlayerAvatar3D(this.scene, this.BABYLON, seat, playerData, {
        chairRoot,
        seatIndex: i,
        dims: this.dims,
        visible: showFigure,
        isComputer,
        isTransparent: isComputer,
        wrapAvatarOnHead: isComputer
      });

      this.seats.set(seat, avatar);
    }

    return this.seats;
  }

  /**
   * Normalizes seat identifier across alias forms (you/cpu/player1/team1 -> p1, etc.).
   * @param {string} seat
   * @returns {string}
   */
  normalizeSeatId(seat) {
    if (!seat) return '';
    if (seat === 'you' || seat === 'player1' || seat === 'team1') return 'p1';
    if (seat === 'cpu' || seat === 'player2' || seat === 'team2') return 'p2';
    if (seat === 'player3' || seat === 'team3') return 'p3';
    if (seat === 'player4' || seat === 'team4') return 'p4';
    return seat;
  }

  /**
   * Retrieves the PlayerAvatar3D instance for a given seat.
   * @param {string} seat - 'p1', 'p2', 'you', 'cpu', etc.
   * @returns {PlayerAvatar3D|null}
   */
  getSeat(seat) {
    const key = this.normalizeSeatId(seat);
    return this.seats.get(key) || this.seats.get(seat) || null;
  }

  /**
   * Updates an individual seated player's data (name, score, avatar, turn).
   * @param {string} seat
   * @param {Object} data
   */
  updatePlayer(seat, data = {}) {
    const avatar = this.getSeat(seat);
    if (avatar) avatar.update(data);
  }

  /**
   * Resolves the numeric score for a specific seat from a scores object,
   * accounting for raw IDs (p1-p8), aliases (you/cpu), teams (team1-team4),
   * and partnership mirroring in 4-player cribbage.
   * @param {string} seat - 'p1', 'p2', 'p3', 'p4', etc.
   * @param {Object} scores - Map of scores
   * @returns {number}
   */
  resolveSeatScore(seat, scores = {}) {
    if (!scores || typeof scores !== 'object') return 0;
    const norm = this.normalizeSeatId(seat);

    // 1. Direct match on raw seat or normalized seat
    if (scores[seat] !== undefined) return Number(scores[seat]) || 0;
    if (scores[norm] !== undefined) return Number(scores[norm]) || 0;

    // 2. Named aliases
    if (norm === 'p1') {
      if (scores.you !== undefined) return Number(scores.you) || 0;
      if (scores.player1 !== undefined) return Number(scores.player1) || 0;
      if (scores.team1 !== undefined) return Number(scores.team1) || 0;
    } else if (norm === 'p2') {
      if (scores.cpu !== undefined) return Number(scores.cpu) || 0;
      if (scores.player2 !== undefined) return Number(scores.player2) || 0;
      if (scores.team2 !== undefined) return Number(scores.team2) || 0;
    } else if (norm === 'p3') {
      if (scores.player3 !== undefined) return Number(scores.player3) || 0;
      if (scores.team3 !== undefined) return Number(scores.team3) || 0;
    } else if (norm === 'p4') {
      if (scores.player4 !== undefined) return Number(scores.player4) || 0;
      if (scores.team4 !== undefined) return Number(scores.team4) || 0;
    }

    // 3. Team / Partnership resolution
    let team = null;
    if (typeof window !== 'undefined' && typeof window.getSeatTeam === 'function') {
      try { team = window.getSeatTeam(norm); } catch (_) {}
    }
    if (!team) {
      // Standard 4-player cribbage: p1 & p3 are team1, p2 & p4 are team2
      const totalSeats = (this.seats && this.seats.size) || 4;
      if (totalSeats === 4) {
        if (norm === 'p1' || norm === 'p3') team = 'team1';
        else if (norm === 'p2' || norm === 'p4') team = 'team2';
      }
    }

    if (team && scores[team] !== undefined) {
      return Number(scores[team]) || 0;
    }

    // 4. Partner mirroring for 4P partnership
    if (norm === 'p3') {
      if (scores.team1 !== undefined) return Number(scores.team1) || 0;
      if (scores.p1 !== undefined) return Number(scores.p1) || 0;
      if (scores.you !== undefined) return Number(scores.you) || 0;
    } else if (norm === 'p4') {
      if (scores.team2 !== undefined) return Number(scores.team2) || 0;
      if (scores.p2 !== undefined) return Number(scores.p2) || 0;
      if (scores.cpu !== undefined) return Number(scores.cpu) || 0;
    } else if (norm === 'p1') {
      if (scores.team1 !== undefined) return Number(scores.team1) || 0;
      if (scores.p3 !== undefined) return Number(scores.p3) || 0;
    } else if (norm === 'p2') {
      if (scores.team2 !== undefined) return Number(scores.team2) || 0;
      if (scores.p4 !== undefined) return Number(scores.p4) || 0;
    }

    // 5. Global state scores fallback if local scores object is empty
    if (typeof window !== 'undefined' && window.state && window.state.scores && window.state.scores !== scores) {
      return this.resolveSeatScore(seat, window.state.scores);
    }

    return 0;
  }

  /**
   * Updates live scores across all seated players.
   * @param {Object} scores - { p1: 12, p2: 18, ... } or { team1: 24, team2: 18 }
   */
  updateScores(scores = {}) {
    if (!scores || typeof scores !== 'object') return;

    this.seats.forEach((avatar, seatId) => {
      if (avatar) {
        const sc = this.resolveSeatScore(seatId, scores);
        avatar.setScore(sc);
      }
    });

    for (const [seat, score] of Object.entries(scores)) {
      const avatar = this.getSeat(seat);
      if (avatar && avatar.playerData.score !== Number(score)) {
        avatar.setScore(Number(score) || 0);
      }
    }
  }

  /**
   * Sets the active turn on a player seat, illuminating their halo and updating status plaques.
   * @param {string} activeSeat
   */
  setActiveTurn(activeSeat) {
    const targetSeat = this.normalizeSeatId(activeSeat);
    this.seats.forEach((avatar, seat) => {
      avatar.setTurn(seat === targetSeat || seat === activeSeat);
    });
  }

  /**
   * Sets the active dealer across all player seats, updating nameplate status badges.
   * @param {string} dealerSeat - 'p1', 'p2', 'you', 'cpu', etc.
   */
  setDealer(dealerSeat) {
    const targetSeat = this.normalizeSeatId(dealerSeat);
    this.seats.forEach((avatar, seat) => {
      avatar.setDealer(seat === targetSeat || seat === dealerSeat);
    });
  }

  /**
   * Displays an emoji reaction above the specified player's seat.
   * @param {string} seat
   * @param {string} emoji
   */
  showReaction(seat, emoji) {
    const avatar = this.getSeat(seat);
    if (avatar) avatar.showReaction(emoji);
  }

  /**
   * Updates transparency across all computer player avatars.
   * @param {boolean} isTransparent
   * @param {number} [alpha=0.6]
   */
  setComputerAvatarsTransparent(isTransparent, alpha = 0.6) {
    this.seats.forEach(avatar => {
      if (avatar.isComputer) {
        avatar.setTransparent(isTransparent, alpha);
      }
    });
  }

  /**
   * Updates head wrap on all computer player avatars.
   * @param {boolean} wrap
   */
  setComputerAvatarsWrapOnHead(wrap) {
    this.seats.forEach(avatar => {
      if (avatar.isComputer) {
        avatar.setWrapAvatarOnHead(wrap);
      }
    });
  }

  /**
   * Cleans up and disposes all seated avatars.
   */
  clearAll() {
    this.seats.forEach(avatar => avatar.dispose());
    this.seats.clear();
  }

  /**
   * Toggles visibility of the local player avatar figure.
   * @param {boolean} visible
   */
  setLocalPlayerVisible(visible) {
    this.options.showLocalPlayer = Boolean(visible);
    const p1Avatar = this.getSeat('p1');
    if (p1Avatar) {
      p1Avatar.setVisible(this.options.showLocalPlayer);
    }
  }

  /**
   * Disposes the SeatManager3D.
   */
  dispose() {
    this.clearAll();
  }
}
